import { useState, useEffect } from "react"
import { useAppDispatch, useAppSelector } from "../../store/hook"
import { DateRangePicker } from "react-date-range"
import { ChevronDown, MinusCircle, AlertTriangle, Filter } from "lucide-react"
import CustomModal from "../common/CustomModal"
import { Gift, CheckCircle, Clock, XCircle } from 'lucide-react';
import { fetchVoucherStats } from "../../features/dashboard/DashboardSlice"
import TopBrandsChart from './charts/TopBrandsChart'
import TransactionChart from './charts/TransactionChart'
import { useLobTimezone } from "../../hooks/useLobTimezone"
import VouchersSection from "./VouchersSection"
import { DashboardFilters, type DashboardFiltersState } from "./DashboardFilters"

const formatDateToLocalString = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getInitialFilters = (timezone: string): DashboardFiltersState => ({
  dateFilterType: "createdAt",
  timezone,
  selectedProgram: [],
  selectedDistributor: [],
  selectedChannel: [],
  selectedProcessor: [],
  selectedState: [],
  selectedRecon: [],
})

function getDefaultDateRange() {
  const now = new Date()
  const lastMonth = new Date()
  lastMonth.setMonth(lastMonth.getMonth() - 1)
  return [{ startDate: lastMonth, endDate: now, key: "selection" }]
}

export const Dashboard = () => {
  const dispatch = useAppDispatch()
  const lobTimezone = useLobTimezone()
  const {
    voucherStats,
    brandsData,
    transactionData,
    dashboardLoading,
    dashboardError,
  } = useAppSelector(state => state.dashboard)

  const now = new Date()
  const lastMonth = new Date()
  lastMonth.setMonth(lastMonth.getMonth() - 1)

  const [dateRange, setDateRange] = useState(() => getDefaultDateRange())
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<DashboardFiltersState>(() => getInitialFilters(lobTimezone.code))

  // Fetch voucher stats when date range or filters change (search is voucher-only, not applied here)
  useEffect(() => {
    const fromDate = formatDateToLocalString(dateRange[0].startDate)
    const toDate = formatDateToLocalString(dateRange[0].endDate)
    const statsParams = {
      fromDate,
      toDate,
      ...(filters.dateFilterType && { dateField : filters.dateFilterType }),
      ...(filters.selectedProgram.length > 0 && { program: filters.selectedProgram }),
      ...(filters.selectedDistributor.length > 0 && { distributor: filters.selectedDistributor }),
      ...(filters.selectedChannel.length > 0 && { channel: filters.selectedChannel }),
      ...(filters.selectedProcessor.length > 0 && { processor: filters.selectedProcessor }),
      ...(filters.selectedState.length > 0 && { state: filters.selectedState }),
      ...(filters.selectedRecon.length > 0 && { recon: filters.selectedRecon }),
      timezone: filters.timezone,
    }
    dispatch(fetchVoucherStats(statsParams))
  }, [dispatch, dateRange, filters])

  const handleSelect = (ranges: any) => {
    setDateRange([ranges.selection])
    setPage(1)
  }

  const handleFiltersChange = (updates: Partial<DashboardFiltersState>) => {
    setFilters((prev) => ({ ...prev, ...updates }))
    setPage(1)
  }

  const clearAllFilters = () => {
    setFilters(getInitialFilters(lobTimezone.code))
    setPage(1)
  }

  const hasActiveFilters =
    filters.selectedProgram.length > 0 ||
    filters.selectedDistributor.length > 0 ||
    filters.selectedChannel.length > 0 ||
    filters.selectedProcessor.length > 0 ||
    filters.selectedState.length > 0 ||
    filters.selectedRecon.length > 0

  // Format date range for display
  const formatDateRange = () => {
    const { startDate, endDate } = dateRange[0]
    const startYear = startDate.getFullYear()
    const endYear = endDate.getFullYear()
    
    if (startYear === endYear) {
      // Same year - don't show year
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    } else {
      // Different years - show year
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    }
  }

  // --- UI Card Data ---
  const stats = [
    {
      label: "Vouchers Issued",
      value: voucherStats?.issued || 0,
      icon: <Gift className="text-green-600 w-6 h-6" strokeWidth={2} />,
      change: "+12%",
      changeColor: "text-emerald-500",
      sub: "from last month",
    },
    {
      label: "Vouchers Activated",
      value: voucherStats?.activated || 0,
      icon: <Clock className="text-orange-500 w-6 h-6" strokeWidth={2} />,
      change: "+5%",
      changeColor: "text-amber-400",
      sub: "from last month",
    },
    {
      label: "Vouchers Used",
      value: voucherStats?.used || 0,
      icon: <CheckCircle className="text-blue-600 w-6 h-6" strokeWidth={2} />,
      change: "0%",
      changeColor: "text-sky-500",
      sub: "from last month",
    },
    {
      label: "Vouchers Expired",
      value: voucherStats?.expired || 0,
      icon: <AlertTriangle className="text-amber-500 w-6 h-6" strokeWidth={2} />,
      change: "0%",
      changeColor: "text-gray-400",
      sub: "from last month",
    },
    {
      label: "Vouchers Failed",
      value: voucherStats?.failed || 0,
      icon: <XCircle className="text-rose-500 w-6 h-6" strokeWidth={2} />,
      change: "0%",
      changeColor: "text-gray-400",
      sub: "from last month",
    },
    {
      label: "Vouchers Revoked",
      value: voucherStats?.revoked || 0,
      icon: <MinusCircle className="text-red-600 w-6 h-6" strokeWidth={2} />,
      change: "0%",
      changeColor: "text-gray-400",
      sub: "from last month",
    },
  ]

  // --- UI ---
  return (
    <div className=" bg-white">
      {/* Header with date range, search, and filters */}
      <div className="border-b border-cardborder mb-8 max-w-7xl mx-auto px-8 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h2 className="text-2xl font-bold text-gray-900">
            Dashboard
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <span className="px-3 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-600 bg-gray-50">
              {filters.timezone}
            </span>
            <button
              type="button"
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-500 bg-white hover:shadow-sm"
              onClick={() => setIsModalOpen(true)}
            >
              <span>{formatDateRange()}</span>
              <ChevronDown className="w-5 h-5" />
            </button>
            <button
              type="button"
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-500 bg-white hover:border-primary-500 focus:outline-none"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="px-3 py-2 text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Clear All
              </button>
            )}
          </div>
        </div>
        {showFilters && (
          <div className="mt-4">
            <DashboardFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onFilterChange={() => setPage(1)}
            />
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 lg:gap-6">
        {stats.map((stat, idx) => (
          <div
            key={stat.label}
            className="bg-white rounded-xl p-4 lg:p-6 flex flex-col justify-center min-h-[120px] hover:shadow-lg transition-shadow duration-200 transition-colors border-[1px] border-cardborder"
          >
            <div className="flex flex-row items-start gap-2 mb-2">
              <span className="text-xs sm:text-sm font-medium text-gray-500 flex-1 leading-tight">{stat.label}</span>
              <div className="ml-auto flex-shrink-0 flex items-center justify-center">
                {stat.icon}
              </div>
            </div>
            <span className="text-2xl sm:text-3xl font-bold text-gray-900">{stat.value}</span>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        <div className="bg-white rounded-xl  p-6 flex flex-col justify-start min-h-[120px]  transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
          <div className="font-semibold text-lg mb-4">Top Brands</div>
          <TopBrandsChart outletsData={brandsData || []} />
        </div>
        <div className="bg-white rounded-xl  p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
          <div className="font-semibold text-lg mb-4">Transaction</div>
          <TransactionChart
            transactionData={transactionData || []}
            fromDate={formatDateToLocalString(dateRange[0].startDate)}
            toDate={formatDateToLocalString(dateRange[0].endDate)}
          />
        </div>
      </div>

      {/* Date Range Modal */}
      <CustomModal
        show={isModalOpen}
        onHide={() => setIsModalOpen(false)}
        title="Date Range"
        width="w-[600px]"
      >
        <div className="flex flex-col items-center">
          <DateRangePicker
            ranges={dateRange}
            onChange={handleSelect}
            maxDate={now}
          />
        </div>
       
      </CustomModal>

      {/* Vouchers Section */}
      <VouchersSection
        dateRange={dateRange}
        page={page}
        onPageChange={setPage}
        dashboardFilters={filters}
      />
    </div>
  )
}
